import { ShieldAlert } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import type { SkippedResource } from '@/types/connectors';

interface Props { resources?: SkippedResource[] | null; }

/** #1880 — warning (non-error) callout listing resources the credential may not
 *  read, which #1879 skips instead of failing the run. A permission gap, not a
 *  failure: the resource is re-attempted every run and heals once the grant is
 *  added, so the provider's message (it names the missing grant and principal)
 *  is shown verbatim as the actionable part.
 *
 *  Since #2134 an entry can cover one account's share of a resource (its message
 *  starts with the account) while the resource's other accounts landed, so the
 *  heading counts distinct resources and the copy does not claim that nothing
 *  was extracted for them. */
export function SkippedResourcesPanel({ resources }: Props) {
  if (!resources?.length) return null;
  const count = new Set(resources.map((s) => s.resource)).size;
  return (
    <Alert variant="warning" data-testid="skipped-resources-panel">
      <ShieldAlert className="h-4 w-4" aria-hidden />
      <AlertTitle>
        {count === 1 ? '1 resource was' : `${count} resources were`} skipped in whole or in part
        — the credential lacks permission
      </AlertTitle>
      <AlertDescription>
        <p className="mb-1.5 text-foreground">
          Nothing was extracted for the entries below. An entry whose message starts with an
          account covers only that account; the resource&apos;s other accounts still landed. The run
          retries each entry every time and includes it once the permission is granted.
        </p>
        <ul className="space-y-1">
          {resources.map((s, i) => (
            <li key={`${s.resource}-${i}`}>
              <span className="font-semibold text-foreground">{s.resource}</span>
              {s.error && (
                <span className="block break-words font-mono text-xs text-foreground">{s.error}</span>
              )}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
